import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Select as ChakraSelect,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react"
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useBlocker } from "@tanstack/react-router"
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { FiAlertCircle, FiDatabase, FiPlus, FiX } from "react-icons/fi"
import {
  checkProductionMaterialAvailability,
  type ProductionMaterialAvailabilityComponent,
  type ProductionMaterialAvailabilityResponse,
} from "../../../api/productionMaterialAvailability"
import {
  type MeldunekCreateDTO,
  type ProductionOrderResponseDTO,
  ProductionOrderService,
  type ProductResponceDTO,
  ProductService,
  type TechnologyResponseDTO,
  TechnologyService,
} from "../../../client"
import useAuth from "../../../hooks/useAuth"
import { hasPermission } from "../../../utils/roles"
import ConfirmDialog from "../../Common/ConfirmDialog"
import UnifiedProductPickerModal from "../Modals/UnifiedProductPickerModal"
import WarehouseLocationSelect from "../Modals/WarehouseLocationSelect"
import { formatNumber } from "./labelUtils"
import { ProductionMaterialAvailabilityUnavailable } from "./ProductionMaterialAvailabilityWarning"

type MaterialAllocationDraft = {
  id: string
  locationCode: string
  warehouseLotId?: string
  lotLabel?: string
}

type RecoveryRefreshState = "idle" | "pending" | "succeeded" | "failed"

type ProductDisplaySummary = {
  sku: string
  name: string
  unit: string
}

type FormattedMeldunekError = {
  message: string
  kind: "shortage" | "other"
}

export type ProductionMeldunekCreateModalProps = {
  orderId: string | null
  isOpen: boolean
  onClose: () => void
  onCreated?: (createdCount: number) => void | Promise<void>
  initialRunCount?: number
  requireActive?: boolean
}

const createAllocation = (productId?: string): MaterialAllocationDraft => ({
  id: productId
    ? `alloc-default-${productId}`
    : `alloc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  locationCode: "",
})

const meldunekShortagePattern =
  /^niewystarczający stan dla produktu\s+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s*:\s*potrzeba\s+([0-9]+(?:[.,][0-9]+)?)\s*,\s*brak\s+([0-9]+(?:[.,][0-9]+)?)$/i

const getMeldunekErrorMessage = (error: any) => {
  const value =
    error?.body?.error ??
    error?.body?.detail ??
    error?.message ??
    "Spróbuj ponownie."
  return typeof value === "string" && value.trim() ? value : "Spróbuj ponownie."
}

const formatMeldunekError = (
  error: any,
  productSummaries: Map<string, ProductDisplaySummary>,
): FormattedMeldunekError => {
  const message = getMeldunekErrorMessage(error)
  const match = message.match(meldunekShortagePattern)
  if (!match) return { message, kind: "other" }

  const requiredQty = Number.parseFloat(match[2].replace(",", "."))
  const missingQty = Number.parseFloat(match[3].replace(",", "."))
  if (
    !Number.isFinite(requiredQty) ||
    !Number.isFinite(missingQty) ||
    requiredQty <= 0 ||
    missingQty < 0
  ) {
    return { message, kind: "other" }
  }

  const coveredQty = Math.min(
    requiredQty,
    Math.max(0, requiredQty - missingQty),
  )
  const product = productSummaries.get(match[1].toLowerCase())
  const productLabel = product ? `${product.sku} · ${product.name}` : null
  const shortagePrefix = product
    ? `Niewystarczający stan materiału ${productLabel}`
    : "Niewystarczający stan wybranego materiału"
  const unit = product?.unit || "jedn."
  const sentenceEnding = unit.endsWith(".") ? "" : "."

  return {
    kind: "shortage",
    message: `${shortagePrefix}. Wymagane: ${formatNumber(requiredQty)} ${unit}; wybrane partie/lokalizacje pokrywają: ${formatNumber(coveredQty)} ${unit}; brakuje: ${formatNumber(missingQty)} ${unit}${sentenceEnding} Dodaj materiał z kolejnej partii lub lokalizacji.`,
  }
}

const addProductSummary = (
  summaries: Map<string, ProductDisplaySummary>,
  productId: string,
  product: { id?: string; sku?: string; name?: string; unit?: string } | null,
) => {
  if (!productId || !product) return
  const summary = {
    sku: product.sku ?? "",
    name: product.name ?? "",
    unit: product.unit ?? "",
  }
  summaries.set(productId.toLowerCase(), summary)
  if (product.id) summaries.set(product.id.toLowerCase(), summary)
}

export default function ProductionMeldunekCreateModal({
  orderId,
  isOpen,
  onClose,
  onCreated,
  initialRunCount,
  requireActive = false,
}: ProductionMeldunekCreateModalProps) {
  const qc = useQueryClient()
  const toast = useToast()
  const { user: currentUser } = useAuth()
  const mutedTextColor = useColorModeValue("gray.500", "gray.400")
  const shortageRowBg = useColorModeValue("red.50", "red.900")
  const picker = useDisclosure()
  const [pickerProductId, setPickerProductId] = useState<string | null>(null)
  const [pickerAllocationId, setPickerAllocationId] = useState<string | null>(
    null,
  )
  const [runCount, setRunCount] = useState("")
  const [note, setNote] = useState("")
  const [outputLocationCode, setOutputLocationCode] = useState("")
  const [scrapMap, setScrapMap] = useState<Record<string, string>>({})
  const [selectedProductPerSlot, setSelectedProductPerSlot] = useState<
    Record<string, string>
  >({})
  const [inputAllocationsMap, setInputAllocationsMap] = useState<
    Record<string, MaterialAllocationDraft[]>
  >({})
  const sequencePendingRef = useRef(false)
  const mountedRef = useRef(true)
  const [isSequencePending, setIsSequencePending] = useState(false)
  const [unknownOutcome, setUnknownOutcome] = useState(false)
  const [partialFailureCount, setPartialFailureCount] = useState(0)
  const [partialFailureReason, setPartialFailureReason] = useState<
    string | null
  >(null)
  const [refreshFailure, setRefreshFailure] = useState(false)
  const [recoveryRefreshState, setRecoveryRefreshState] =
    useState<RecoveryRefreshState>("idle")

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      sequencePendingRef.current = false
    }
  }, [])

  const orderQuery = useQuery<ProductionOrderResponseDTO>({
    queryKey: ["production-orders", "detail", orderId],
    queryFn: () => ProductionOrderService.getProductionOrderByID(orderId!),
    enabled: isOpen && !!orderId,
  })
  const { data: order, isPending: isOrderPending } = orderQuery
  const technologyQuery = useQuery<TechnologyResponseDTO>({
    queryKey: ["technology", "detail", order?.technology_id],
    queryFn: () => TechnologyService.getTechnologyByID(order!.technology_id),
    enabled: isOpen && !!order?.technology_id,
  })
  const { data: technology, isPending: isTechnologyPending } = technologyQuery
  const productIds = useMemo(() => {
    const ids = new Set<string>()
    for (const input of technology?.inputs ?? []) {
      ids.add(input.product_id)
      for (const substitute of input.substitutes ?? []) {
        ids.add(substitute.substitute_product_id)
      }
    }
    for (const output of technology?.outputs ?? []) ids.add(output.product_id)
    return [...ids]
  }, [technology])
  const productQueries = useQueries({
    queries: productIds.map((productId) => ({
      queryKey: ["products", "detail", productId],
      queryFn: () => ProductService.getProductByID(productId),
      enabled: isOpen,
    })),
  })
  const productMap = useMemo(() => {
    const map = new Map<string, ProductResponceDTO>()
    for (const result of productQueries) {
      if (result.data?.id) map.set(result.data.id, result.data)
    }
    return map
  }, [productQueries])
  const productSummaries = useMemo(() => {
    const summaries = new Map<string, ProductDisplaySummary>()
    for (const input of technology?.inputs ?? []) {
      addProductSummary(summaries, input.product_id, input.product)
      for (const substitute of input.substitutes ?? []) {
        addProductSummary(
          summaries,
          substitute.substitute_product_id,
          substitute.product,
        )
      }
    }
    for (const [productId, product] of productMap) {
      addProductSummary(summaries, productId, product)
    }
    return summaries
  }, [productMap, technology])

  const deferredRunCount = useDeferredValue(runCount)
  const deferredSelectedProductPerSlot = useDeferredValue(
    selectedProductPerSlot,
  )
  const deferredScrapMap = useDeferredValue(scrapMap)
  const availabilityRequest = useMemo(() => {
    const numericRunCount = Number.parseFloat(deferredRunCount)
    if (
      !order ||
      !technology ||
      !Number.isFinite(numericRunCount) ||
      numericRunCount <= 0
    ) {
      return null
    }
    return {
      checks: [
        {
          order_id: order.id,
          run_count: numericRunCount,
          selections: technology.inputs.map((input) => {
            const productId =
              deferredSelectedProductPerSlot[input.id] ?? input.product_id
            return {
              technology_input_id: input.id,
              product_id: productId,
              scrap_qty:
                Number.parseFloat(deferredScrapMap[productId] ?? "0") || 0,
            }
          }),
        },
      ],
    }
  }, [
    deferredRunCount,
    deferredScrapMap,
    deferredSelectedProductPerSlot,
    order,
    technology,
  ])
  const materialAvailabilityQuery =
    useQuery<ProductionMaterialAvailabilityResponse>({
      queryKey: ["production-material-availability", availabilityRequest],
      queryFn: () => checkProductionMaterialAvailability(availabilityRequest!),
      enabled:
        isOpen &&
        Boolean(availabilityRequest) &&
        hasPermission(currentUser, "wms.production_order.read") &&
        hasPermission(currentUser, "wms.warehouse.read"),
      retry: false,
    })
  const materialAvailabilityResult = materialAvailabilityQuery.isError
    ? undefined
    : materialAvailabilityQuery.data?.checks[0]
  const shortageByInputId = useMemo(() => {
    const map = new Map<string, ProductionMaterialAvailabilityComponent>()
    for (const component of materialAvailabilityResult?.components ?? []) {
      if (component.status !== "shortage" || component.missing_qty <= 0) {
        continue
      }
      const inputIds =
        component.technology_input_ids &&
        component.technology_input_ids.length > 0
          ? component.technology_input_ids
          : [component.technology_input_id]
      for (const inputId of inputIds) map.set(inputId, component)
    }
    return map
  }, [materialAvailabilityResult])

  const mainOutput = useMemo(
    () =>
      technology?.outputs?.find((output) => output.is_main) ??
      technology?.outputs?.[0],
    [technology],
  )
  const remaining = useMemo(() => {
    if (!order || !mainOutput || mainOutput.qty <= 0) return 0
    return Math.max(order.target_qty - order.produced_qty, 0) / mainOutput.qty
  }, [mainOutput, order])
  const effectiveMaxRunCount = Math.max(0, remaining)

  useEffect(() => {
    if (!isOpen) return
    setRunCount(initialRunCount !== undefined ? `${initialRunCount}` : "")
    setNote("")
    setOutputLocationCode("")
    setScrapMap({})
    setSelectedProductPerSlot({})
    setInputAllocationsMap({})
    setPickerProductId(null)
    setPickerAllocationId(null)
    setUnknownOutcome(false)
    setPartialFailureCount(0)
    setPartialFailureReason(null)
    setRefreshFailure(false)
    setRecoveryRefreshState("idle")
  }, [initialRunCount, isOpen])

  const getInputAllocations = useCallback(
    (productId: string) =>
      inputAllocationsMap[productId] ?? [createAllocation(productId)],
    [inputAllocationsMap],
  )
  const updateAllocation = (
    productId: string,
    allocationId: string,
    update: (allocation: MaterialAllocationDraft) => MaterialAllocationDraft,
  ) => {
    setInputAllocationsMap((current) => ({
      ...current,
      [productId]: getInputAllocations(productId).map((allocation) =>
        allocation.id === allocationId ? update(allocation) : allocation,
      ),
    }))
  }
  const addAllocation = (productId: string) => {
    setInputAllocationsMap((current) => ({
      ...current,
      [productId]: [...getInputAllocations(productId), createAllocation()],
    }))
  }
  const removeAllocation = (productId: string, allocationId: string) => {
    const next = getInputAllocations(productId).filter(
      (allocation) => allocation.id !== allocationId,
    )
    setInputAllocationsMap((current) => ({
      ...current,
      [productId]: next.length > 0 ? next : [createAllocation(productId)],
    }))
  }

  const plannedInputs = useMemo(() => {
    const factor = Number.parseFloat(runCount)
    if (!technology || !Number.isFinite(factor) || factor <= 0) return []
    return technology.inputs.map((input) => {
      const productId = selectedProductPerSlot[input.id] ?? input.product_id
      const substitute = input.substitutes?.find(
        (candidate) => candidate.substitute_product_id === productId,
      )
      const qty = substitute?.qty ?? input.qty
      const loss = substitute?.loss_percent ?? input.loss_percent ?? 0
      const plannedQty = qty * factor * (1 + loss / 100)
      const scrapQty = Number.parseFloat(scrapMap[productId] ?? "0") || 0
      return {
        slotId: input.id,
        productId,
        plannedQty,
        usedQty: plannedQty + scrapQty,
      }
    })
  }, [runCount, scrapMap, selectedProductPerSlot, technology])

  const buildItems = (
    scrapValues: Record<string, string | number> = scrapMap,
  ) =>
    (technology?.inputs ?? []).flatMap((input) => {
      const productId = selectedProductPerSlot[input.id] ?? input.product_id
      const scrapQty =
        Number.parseFloat(String(scrapValues[productId] ?? "0")) || 0
      if (productMap.get(productId)?.is_service) {
        return [
          { product_id: productId, location_code: "", scrap_qty: scrapQty },
        ]
      }
      return getInputAllocations(productId)
        .filter((allocation) => allocation.locationCode.trim())
        .filter(
          (allocation, index, all) =>
            all.findIndex(
              (candidate) =>
                candidate.locationCode.trim() ===
                  allocation.locationCode.trim() &&
                (candidate.warehouseLotId ?? "") ===
                  (allocation.warehouseLotId ?? ""),
            ) === index,
        )
        .map((allocation, index) => ({
          product_id: productId,
          location_code: allocation.locationCode.trim(),
          ...(allocation.warehouseLotId
            ? { warehouse_lot_id: allocation.warehouseLotId }
            : {}),
          scrap_qty: index === 0 ? scrapQty : 0,
        }))
    })

  const canSubmit = useMemo(() => {
    const quantity = Number.parseFloat(runCount)
    if (!order || !technology) return false
    if (
      requireActive ? order.status !== "active" : order.status === "cancelled"
    )
      return false
    if (!Number.isFinite(quantity) || quantity <= 0) return false
    if (quantity > effectiveMaxRunCount || !outputLocationCode.trim())
      return false
    return technology.inputs.every((input) => {
      const productId = selectedProductPerSlot[input.id] ?? input.product_id
      return (
        productMap.get(productId)?.is_service ||
        getInputAllocations(productId).some((allocation) =>
          allocation.locationCode.trim(),
        )
      )
    })
  }, [
    effectiveMaxRunCount,
    getInputAllocations,
    order,
    outputLocationCode,
    productMap,
    runCount,
    selectedProductPerSlot,
    technology,
    requireActive,
  ])

  const formatMeldunekCount = (count: number) => {
    const modulo100 = count % 100
    const modulo10 = count % 10
    const noun =
      count === 1
        ? "meldunek"
        : modulo10 >= 2 && modulo10 <= 4 && (modulo100 < 12 || modulo100 > 14)
          ? "meldunki"
          : "meldunków"
    return `${count} ${noun}`
  }
  const formatMinimumMeldunekCount = (count: number) =>
    count === 1
      ? "co najmniej jednego meldunku"
      : `co najmniej ${count} meldunków`
  const createMutation = useMutation({
    mutationKey: ["production-orders", "meldunek", orderId],
    mutationFn: (payload: MeldunekCreateDTO) =>
      ProductionOrderService.createMeldunek(orderId!, payload),
    retry: false,
  })

  const handleClose = useCallback(() => {
    if (sequencePendingRef.current) return
    onClose()
  }, [onClose])

  const blocker = useBlocker({
    disabled: !isSequencePending,
    enableBeforeUnload: isSequencePending,
    withResolver: true,
    shouldBlockFn: () => true,
  })

  const invalidateKnownSequenceQueries = async () => {
    await Promise.all([
      qc.invalidateQueries({
        queryKey: ["production-orders", "detail", orderId],
      }),
      qc.invalidateQueries({
        queryKey: ["production-orders", "meldunki", orderId],
      }),
      qc.invalidateQueries({ queryKey: ["production-orders", "list"] }),
      qc.invalidateQueries({ queryKey: ["production-chain"] }),
    ])
  }

  const refreshAuthoritativeSequenceQueries = async () => {
    if (mountedRef.current) setRecoveryRefreshState("pending")
    const results = await Promise.allSettled([
      qc.invalidateQueries(
        {
          queryKey: ["production-orders", "detail", orderId],
        },
        { throwOnError: true },
      ),
      qc.invalidateQueries(
        {
          queryKey: ["production-orders", "meldunki", orderId],
        },
        { throwOnError: true },
      ),
      qc.invalidateQueries(
        { queryKey: ["production-orders", "list"] },
        { throwOnError: true },
      ),
      qc.fetchQuery({
        queryKey: ["production-orders", "meldunki", orderId, "verification"],
        queryFn: () =>
          ProductionOrderService.getMeldunkiByOrder(orderId!, 1, 100),
      }),
      qc.refetchQueries(
        { queryKey: ["production-chain"] },
        { throwOnError: true },
      ),
    ])
    const succeeded = results.every((result) => result.status === "fulfilled")
    if (mountedRef.current) {
      setRecoveryRefreshState(succeeded ? "succeeded" : "failed")
    }
    return succeeded
  }

  const submit = async () => {
    if (
      !canSubmit ||
      createMutation.isPending ||
      sequencePendingRef.current ||
      unknownOutcome ||
      partialFailureCount > 0 ||
      refreshFailure
    )
      return
    sequencePendingRef.current = true
    setIsSequencePending(true)
    setRefreshFailure(false)
    setRecoveryRefreshState("idle")
    const quantity = Number.parseFloat(runCount)
    const pieces: number[] = []
    let remainingRuns = quantity
    while (remainingRuns > 0) {
      const piece = Math.min(1, remainingRuns)
      pieces.push(piece)
      remainingRuns -= piece
    }
    const remainingScrap = Object.fromEntries(
      Object.entries(scrapMap).map(([productId, value]) => [
        productId,
        Number.parseFloat(value) || 0,
      ]),
    )
    const originalScrap = { ...remainingScrap }
    let createdCount = 0
    try {
      for (const [pieceIndex, piece] of pieces.entries()) {
        if (!mountedRef.current) {
          await invalidateKnownSequenceQueries()
          return
        }
        const ratio = piece / quantity
        const pieceScrapMap = Object.fromEntries(
          Object.entries(remainingScrap).map(([productId, total]) => {
            const pieceScrap =
              pieceIndex === pieces.length - 1
                ? remainingScrap[productId]
                : originalScrap[productId] * ratio
            remainingScrap[productId] = total - pieceScrap
            return [productId, pieceScrap]
          }),
        )
        await createMutation.mutateAsync({
          run_count: piece,
          is_partial: piece < 1 ? true : undefined,
          output_location_code: outputLocationCode.trim(),
          note: note.trim() || undefined,
          items: buildItems(pieceScrapMap),
        })
        createdCount += 1
        if (!mountedRef.current) {
          await invalidateKnownSequenceQueries()
          return
        }
      }
      const refreshed = await refreshAuthoritativeSequenceQueries()
      if (!refreshed) {
        setRefreshFailure(true)
        toast({
          title: "Meldunki zostały utworzone",
          description:
            "Utworzono wszystkie meldunki, ale nie udało się w pełni odświeżyć danych. Zamknij okno i zweryfikuj zlecenie przed kolejną próbą.",
          status: "warning",
          duration: 9000,
        })
        return
      }
      toast({
        title:
          pieces.length > 1
            ? "Meldunki zostały utworzone"
            : "Meldunek utworzony",
        status: "success",
      })
      await onCreated?.(pieces.length)
      onClose()
    } catch (error: any) {
      if (!mountedRef.current) {
        await invalidateKnownSequenceQueries()
        return
      }
      const formattedError = formatMeldunekError(error, productSummaries)
      const status = error?.status ?? error?.response?.status
      if (createdCount === pieces.length && pieces.length > 0) {
        setRefreshFailure(true)
        const refreshed = await refreshAuthoritativeSequenceQueries()
        toast({
          title: "Meldunki zostały utworzone",
          description: refreshed
            ? "Utworzono wszystkie meldunki i odświeżono dane. Zweryfikuj zlecenie przed kolejną próbą."
            : "Utworzono wszystkie meldunki, ale nie udało się w pełni odświeżyć danych. Zamknij okno i zweryfikuj zlecenie przed kolejną próbą.",
          status: "warning",
          duration: 9000,
        })
      } else if (!status && !error?.body) {
        setUnknownOutcome(true)
        setPartialFailureCount(createdCount)
        const refreshed = await refreshAuthoritativeSequenceQueries()
        toast({
          title:
            createdCount > 0
              ? "Niepewny wynik części meldunków"
              : "Niepewny wynik meldunku",
          description:
            createdCount > 0
              ? `Potwierdzono utworzenie ${formatMinimumMeldunekCount(createdCount)}. Wynik kolejnego meldunku jest nieznany. ${refreshed ? "Dane zostały odświeżone." : "Nie udało się w pełni odświeżyć danych."} Zweryfikuj zlecenie przed kolejną próbą.`
              : `${refreshed ? "Dane zostały odświeżone." : "Nie udało się w pełni odświeżyć danych."} Sprawdź, czy meldunek został utworzony przed ponowną próbą.`,
          status: "warning",
          duration: 9000,
        })
      } else if (createdCount > 0) {
        setPartialFailureCount(createdCount)
        const reason =
          formattedError.kind === "shortage" ? formattedError.message : null
        setPartialFailureReason(reason)
        const refreshed = await refreshAuthoritativeSequenceQueries()
        toast({
          title: "Nie udało się utworzyć wszystkich meldunków",
          description: `Utworzono już ${formatMeldunekCount(createdCount)}.${reason ? ` Powód zatrzymania: ${reason}` : ""} ${refreshed ? "Dane zostały odświeżone." : "Nie udało się w pełni odświeżyć danych."} Zamknij okno i zweryfikuj dane przed kolejną próbą.`,
          status: "warning",
          duration: 9000,
        })
      } else {
        toast({
          title: "Błąd meldunku",
          description: formattedError.message,
          status: "error",
        })
      }
    } finally {
      sequencePendingRef.current = false
      if (mountedRef.current) setIsSequencePending(false)
    }
  }

  const recoveryRefreshMessage =
    recoveryRefreshState === "pending"
      ? "Trwa odświeżanie danych."
      : recoveryRefreshState === "succeeded"
        ? "Dane zostały odświeżone."
        : recoveryRefreshState === "failed"
          ? "Nie udało się w pełni odświeżyć danych."
          : "Trwa sprawdzanie danych."

  const queryError = orderQuery.error ?? technologyQuery.error
  const isLoading =
    isOrderPending || isTechnologyPending || !order || !technology
  const pickerAllocations = pickerProductId
    ? getInputAllocations(pickerProductId)
    : []

  return (
    <>
      {blocker.status === "blocked" && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => blocker.reset()}
          onConfirm={() => blocker.reset()}
          title="Trwa tworzenie meldunków"
          message="Nie można opuścić strony, gdy trwa tworzenie meldunków. Poczekaj na zakończenie sekwencji."
          confirmLabel="Pozostań na stronie"
          cancelLabel="Anuluj"
          confirmColorScheme="blue"
        />
      )}
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        closeOnEsc={!isSequencePending}
        closeOnOverlayClick={!isSequencePending}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Nowy meldunek</ModalHeader>
          <ModalCloseButton isDisabled={isSequencePending} />
          <ModalBody pb={6}>
            {queryError ? (
              <Alert status="error" flexDirection="column" alignItems="start">
                <AlertIcon />
                <Text mb={3}>
                  Nie udało się wczytać danych zlecenia lub technologii. Spróbuj
                  ponownie.
                </Text>
                <Button
                  size="sm"
                  onClick={() => {
                    void orderQuery.refetch()
                    if (order?.technology_id) void technologyQuery.refetch()
                  }}
                >
                  Spróbuj ponownie
                </Button>
              </Alert>
            ) : isLoading ? (
              <HStack justify="center" py={12}>
                <Spinner />
                <Text>Ładowanie danych zlecenia i technologii…</Text>
              </HStack>
            ) : (
              <Stack spacing={4}>
                {unknownOutcome && (
                  <Alert status="warning">
                    <AlertIcon />
                    <Text>
                      {partialFailureCount > 0
                        ? `Potwierdzono utworzenie ${formatMinimumMeldunekCount(partialFailureCount)}. Wynik kolejnego meldunku jest nieznany. ${recoveryRefreshMessage} Zweryfikuj meldunki i zamknij to okno przed kolejną próbą.`
                        : `Niepewny wynik meldunku. ${recoveryRefreshMessage} Zweryfikuj meldunki i zamknij to okno przed rozpoczęciem nowej próby.`}
                    </Text>
                  </Alert>
                )}
                {refreshFailure && !unknownOutcome && (
                  <Alert status="warning">
                    <AlertIcon />
                    <Text>
                      Utworzono wszystkie meldunki. {recoveryRefreshMessage}{" "}
                      Zamknij to okno i zweryfikuj zlecenie przed kolejną próbą.
                    </Text>
                  </Alert>
                )}
                {partialFailureCount > 0 && !unknownOutcome && (
                  <Alert status="warning">
                    <AlertIcon />
                    <Text>
                      Utworzono już {formatMeldunekCount(partialFailureCount)},
                      ale nie udało się zakończyć całej sekwencji.{" "}
                      {partialFailureReason && (
                        <>Powód zatrzymania: {partialFailureReason} </>
                      )}
                      {recoveryRefreshMessage} Zamknij to okno i sprawdź
                      pozostałą produkcję przed kolejną próbą.
                    </Text>
                  </Alert>
                )}
                {materialAvailabilityQuery.isError &&
                  hasPermission(currentUser, "wms.warehouse.read") && (
                    <ProductionMaterialAvailabilityUnavailable />
                  )}
                <Alert status="info">
                  <AlertIcon />
                  <Text>
                    Rejestrujesz produkcję dla zlecenia {order.number}. Każda
                    pełna realizacja utworzy osobny meldunek, a ułamkowa część
                    zostanie zapisana jako ostatni meldunek częściowy. Wybierz
                    dokładne partie i lokalizacje materiałów.
                  </Text>
                </Alert>
                <HStack align="start" spacing={4}>
                  <Box flex={1}>
                    <Text fontWeight="semibold" mb={1}>
                      Liczba realizacji technologii
                    </Text>
                    <NumberInput
                      size="sm"
                      min={0}
                      max={effectiveMaxRunCount}
                      step={1}
                      value={runCount}
                      onChange={setRunCount}
                    >
                      <NumberInputField />
                      <NumberInputStepper aria-hidden={false}>
                        <NumberIncrementStepper aria-label="Zwiększ liczbę realizacji technologii" />
                        <NumberDecrementStepper aria-label="Zmniejsz liczbę realizacji technologii" />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontSize="xs" color={mutedTextColor} mt={1}>
                      Pozostało: {formatNumber(effectiveMaxRunCount)}
                    </Text>
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="semibold" mb={1}>
                      Uwagi
                    </Text>
                    <Textarea
                      size="sm"
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                    />
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="semibold" mb={1}>
                      Lokalizacja produktów wyjściowych
                    </Text>
                    <WarehouseLocationSelect
                      size="sm"
                      warehouseId={order.output_warehouse_id}
                      productId={mainOutput?.product_id}
                      value={outputLocationCode}
                      onChange={setOutputLocationCode}
                      title="Wybierz lokalizację produktów wyjściowych"
                    />
                  </Box>
                </HStack>

                {(technology.outputs ?? []).length > 0 && (
                  <Box>
                    <Heading size="sm" mb={2}>
                      Wyjścia wg technologii
                    </Heading>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Produkt</Th>
                          <Th isNumeric>Ilość</Th>
                          <Th>Rola</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {technology.outputs.map((output) => (
                          <Tr key={output.product_id}>
                            <Td>
                              {productMap.get(output.product_id)?.sku ??
                                output.product_id}{" "}
                              · {productMap.get(output.product_id)?.name ?? "—"}
                            </Td>
                            <Td isNumeric>
                              {formatNumber(
                                output.qty * (Number.parseFloat(runCount) || 0),
                              )}
                            </Td>
                            <Td>
                              {output.is_main ? "Referencyjny" : "Dodatkowy"}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                <Heading size="sm" display="flex" alignItems="center">
                  <Icon as={FiDatabase} mr={2} /> Materiały wg technologii
                </Heading>
                <Table size="sm" width="100%" sx={{ tableLayout: "fixed" }}>
                  <Thead>
                    <Tr>
                      <Th width="26%">Produkt / zamiennik</Th>
                      <Th width="46%">Lokalizacje / partie</Th>
                      <Th width="14%">Błąd / brak</Th>
                      <Th width="14%">Zużycie</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {technology.inputs.map((input) => {
                      const productId =
                        selectedProductPerSlot[input.id] ?? input.product_id
                      const product = productMap.get(productId)
                      const planned = plannedInputs.find(
                        (row) => row.slotId === input.id,
                      )
                      const allocations = getInputAllocations(productId)
                      const shortage = shortageByInputId.get(input.id)
                      const shortageLabel = shortage
                        ? `Brak materiału: ${product?.sku ?? productId} · ${product?.name ?? "—"}. Wymagane: ${formatNumber(shortage.required_qty)} ${shortage.product.unit}; dostępne: ${formatNumber(shortage.available_qty)} ${shortage.product.unit}; Brakuje ${formatNumber(shortage.missing_qty)} ${shortage.product.unit}.`
                        : undefined
                      return (
                        <Tr
                          key={input.id}
                          bg={shortage ? shortageRowBg : undefined}
                          data-material-shortage={shortage ? "true" : "false"}
                          data-testid={`production-material-row-${input.id}`}
                        >
                          <Td overflow="hidden">
                            <HStack spacing={2} minW={0} align="center">
                              {shortage && shortageLabel && (
                                <Tooltip label={shortageLabel} hasArrow>
                                  <Icon
                                    as={FiAlertCircle}
                                    color="red.500"
                                    flexShrink={0}
                                    role="img"
                                    aria-label={shortageLabel}
                                    data-testid={`production-material-shortage-indicator-${input.id}`}
                                  />
                                </Tooltip>
                              )}
                              <Box flex={1} minW={0}>
                                {(input.substitutes ?? []).length > 0 ? (
                                  <ChakraSelect
                                    size="sm"
                                    width="100%"
                                    minW={0}
                                    textOverflow="ellipsis"
                                    value={productId}
                                    title={product?.name ?? productId}
                                    onChange={(event) =>
                                      setSelectedProductPerSlot((current) => ({
                                        ...current,
                                        [input.id]: event.target.value,
                                      }))
                                    }
                                  >
                                    <option value={input.product_id}>
                                      Główny:{" "}
                                      {productMap.get(input.product_id)?.name ??
                                        input.product_id}
                                    </option>
                                    {(input.substitutes ?? []).map(
                                      (substitute) => (
                                        <option
                                          key={substitute.id}
                                          value={
                                            substitute.substitute_product_id
                                          }
                                        >
                                          Zamiennik:{" "}
                                          {productMap.get(
                                            substitute.substitute_product_id,
                                          )?.name ??
                                            substitute.substitute_product_id}
                                        </option>
                                      ),
                                    )}
                                  </ChakraSelect>
                                ) : (
                                  <Text noOfLines={1} title={product?.name}>
                                    {product?.sku ?? productId} ·{" "}
                                    {product?.name ?? "—"}
                                  </Text>
                                )}
                              </Box>
                            </HStack>
                          </Td>
                          <Td>
                            {product?.is_service ? (
                              <Text color={mutedTextColor}>
                                Nie dotyczy dla usługi
                              </Text>
                            ) : (
                              <Stack spacing={2}>
                                {allocations.map((allocation, index) => (
                                  <HStack key={allocation.id} align="start">
                                    <Box flex={1}>
                                      <Input
                                        size="sm"
                                        value={allocation.locationCode}
                                        placeholder="Wybierz lokalizację / partię"
                                        isReadOnly
                                        onClick={() => {
                                          setPickerProductId(productId)
                                          setPickerAllocationId(allocation.id)
                                          picker.onOpen()
                                        }}
                                      />
                                      {allocation.warehouseLotId && (
                                        <Text
                                          fontSize="xs"
                                          color={mutedTextColor}
                                        >
                                          Partia:{" "}
                                          {allocation.lotLabel ??
                                            allocation.warehouseLotId}
                                        </Text>
                                      )}
                                    </Box>
                                    {index === allocations.length - 1 && (
                                      <IconButton
                                        size="sm"
                                        aria-label="Dodaj źródło"
                                        icon={<FiPlus />}
                                        onClick={() => addAllocation(productId)}
                                      />
                                    )}
                                    <IconButton
                                      size="sm"
                                      colorScheme="red"
                                      variant="ghost"
                                      aria-label="Usuń źródło"
                                      icon={<FiX />}
                                      isDisabled={allocations.length === 1}
                                      onClick={() =>
                                        removeAllocation(
                                          productId,
                                          allocation.id,
                                        )
                                      }
                                    />
                                  </HStack>
                                ))}
                              </Stack>
                            )}
                          </Td>
                          <Td>
                            <Input
                              size="sm"
                              type="number"
                              step="any"
                              value={scrapMap[productId] ?? ""}
                              onChange={(event) =>
                                setScrapMap((current) => ({
                                  ...current,
                                  [productId]: event.target.value,
                                }))
                              }
                            />
                          </Td>
                          <Td isNumeric>
                            {formatNumber(planned?.usedQty ?? 0)}
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={handleClose}
              isDisabled={isSequencePending}
              mr={3}
            >
              Anuluj
            </Button>
            <Button
              colorScheme="green"
              isLoading={createMutation.isPending || isSequencePending}
              isDisabled={
                isLoading ||
                !canSubmit ||
                unknownOutcome ||
                partialFailureCount > 0 ||
                refreshFailure ||
                isSequencePending
              }
              onClick={submit}
            >
              Utwórz meldunek i zarejestruj partię
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <UnifiedProductPickerModal
        isOpen={picker.isOpen}
        onClose={() => {
          setPickerProductId(null)
          setPickerAllocationId(null)
          picker.onClose()
        }}
        warehouseId={order?.input_warehouse_id}
        title="Wybierz lokalizację materiału (multywybór)"
        productIdFilter={pickerProductId ?? undefined}
        multiSelect
        disabledLotIds={pickerAllocations
          .filter((allocation) => allocation.id !== pickerAllocationId)
          .map((allocation) => allocation.warehouseLotId)
          .filter((lotId): lotId is string => !!lotId)}
        onChoose={(productId, locationCode, lotId, lotNo) => {
          if (
            !pickerProductId ||
            !pickerAllocationId ||
            productId !== pickerProductId
          )
            return
          updateAllocation(productId, pickerAllocationId, (allocation) => ({
            ...allocation,
            locationCode: locationCode ?? "",
            warehouseLotId: lotId,
            lotLabel: lotNo,
          }))
          setPickerProductId(null)
          setPickerAllocationId(null)
          picker.onClose()
        }}
        onChooseMany={(selections) => {
          if (!pickerProductId || selections.length === 0) return
          setInputAllocationsMap((current) => {
            const existing = getInputAllocations(pickerProductId)
            const next = [...existing]
            selections.forEach((selection, index) => {
              const value = {
                locationCode: selection.locationCode ?? "",
                warehouseLotId: selection.lotId,
                lotLabel: selection.lotNo,
              }
              const targetIndex =
                index === 0 && pickerAllocationId
                  ? next.findIndex(
                      (allocation) => allocation.id === pickerAllocationId,
                    )
                  : next.findIndex(
                      (allocation) =>
                        !allocation.locationCode && !allocation.warehouseLotId,
                    )
              if (targetIndex >= 0)
                next[targetIndex] = { ...next[targetIndex], ...value }
              else next.push({ ...createAllocation(), ...value })
            })
            return { ...current, [pickerProductId]: next }
          })
          setPickerProductId(null)
          setPickerAllocationId(null)
          picker.onClose()
        }}
      />
    </>
  )
}
